import { useMemo } from "react";
import { CodeMirrorEditor } from "./CodeMirrorEditor";
import { mongo } from "../editor/lang-mongo";
import { mongoCompletion, type MongoCompletionConfig } from "../editor/lang-mongo/complete";
import { mongoLinter, type MongoLintConfig } from "../editor/lang-mongo/lint";
import { linter } from "@codemirror/lint";
import { autocompletion } from "@codemirror/autocomplete";
import { useSchemaStore, useServersStore } from "../store";
import type { MongoOperation } from "@spuerhund/shared/targets";

interface MongoEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onExecute?: () => void;
  collection: string;
  operation: MongoOperation;
}

/** Extract fieldPaths and indexNames for a collection from the catalog. */
function useMongoCatalog(serverId: string | null, collection: string) {
  const schemaCache = useSchemaStore((s) => s.schemaCache);

  return useMemo(() => {
    if (!serverId) return { fieldPaths: [], indexNames: [] };
    const cached = schemaCache[serverId];
    if (!cached?.catalogJson || cached.catalogKind !== "mongo-catalog") {
      return { fieldPaths: [], indexNames: [] };
    }
    try {
      const catalog = JSON.parse(cached.catalogJson) as {
        collections?: { name: string; fieldPaths?: string[]; indexes?: { name: string }[] }[];
      };
      const col = catalog.collections?.find((c) => c.name === collection);
      return {
        fieldPaths: col?.fieldPaths ?? [],
        indexNames: (col?.indexes ?? []).map((i) => i.name).filter((n) => n !== "_id_"),
      };
    } catch {
      return { fieldPaths: [], indexNames: [] };
    }
  }, [serverId, schemaCache, collection]);
}

export function MongoEditor({ value, onChange, placeholder, onExecute, collection, operation }: MongoEditorProps) {
  const activeServerId = useServersStore((s) => s.activeServerId);
  const { fieldPaths, indexNames } = useMongoCatalog(activeServerId, collection);

  const extensions = useMemo(() => {
    const completionConfig: MongoCompletionConfig = {
      fieldPaths,
      indexNames,
      operation,
    };

    const lintConfig: MongoLintConfig = {
      fieldPaths,
      operation,
    };

    return [
      mongo(),
      autocompletion({
        override: [mongoCompletion(completionConfig)],
      }),
      linter(mongoLinter(lintConfig)),
    ];
  }, [fieldPaths, indexNames, operation]);

  return (
    <CodeMirrorEditor
      value={value}
      onChange={onChange}
      extensions={extensions}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(onExecute !== undefined ? { onExecute } : {})}
    />
  );
}
